import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthRedirect } from '../utils/authRedirect';
import { useUser } from '../context/UserContext';
import AdSenseAd from './AdSenseAd';

function SavedRecipeDetail() {
    const { id } = useParams();
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newTag, setNewTag] = useState('');
    const newTagRef = useRef(null);

    const handleAuthError = useAuthRedirect();
    const { user, loading: userLoading } = useUser();
    const shouldShowAds = !userLoading && user?.hasNoAds === false;

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const token = localStorage.getItem('token');
                const recipeResponse = await fetch(`/api/recipes/${id}`, {
                    headers: {
                        'x-auth-token': token,
                    },
                });

                if (handleAuthError(recipeResponse.status)) {
                    return; // Redirected, stop further processing
                }

                if (!recipeResponse.ok) {
                    const errData = await recipeResponse.json();
                    throw new Error(errData.error || 'Something went wrong while fetching the recipe.');
                }

                const data = await recipeResponse.json();
                setRecipe(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipe();
    }, [handleAuthError, id]);

    const handleAddTag = async () => {
        if (newTag.trim() === '') return;

        try {
            const token = localStorage.getItem('token');
            const updatedTags = [...recipe.tags, newTag.trim()];
            const response = await fetch(`/api/recipes/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ tags: updatedTags }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to add tag.');
            }

            setRecipe({ ...recipe, tags: updatedTags });
            setNewTag('');
            if (newTagRef && newTagRef.current) newTagRef.current.focus();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleRemoveTag = async (tagToRemove) => {
        try {
            const token = localStorage.getItem('token');
            const updatedTags = recipe.tags.filter(tag => tag !== tagToRemove);
            const response = await fetch(`/api/recipes/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ tags: updatedTags }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to remove tag.');
            }

            setRecipe({ ...recipe, tags: updatedTags });
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) {
        return <p>Loading recipe...</p>;
    }

    if (error) {
        return <div className="alert alert-danger" role="alert">{error}</div>;
    }

    if (!recipe) {
        return <p className="text-center">Recipe not found.</p>;
    }

    return (
        <div className="page saved-recipe-page">
            {shouldShowAds && <AdSenseAd className="ad-slot--inline" minHeight={250} />}

            <header className="page-header">
                <span className="page-eyebrow">Saved recipe</span>
                <h1>{recipe.title}</h1>
            </header>

            <div className="card saved-recipe-card">
                <div className="card-body">
                    <div className="mb-4">
                        <h3>Tags</h3>
                        <div className="saved-recipe-meta mb-3">
                            <div aria-live="polite" className="sr-only">{`You have ${recipe.tags.length} tags`}</div>
                            {recipe.tags.length === 0 ? (
                                <span className="text-muted">No tags yet. Create your first tag below.</span>
                            ) : (
                                recipe.tags.map((tag, index) => (
                                    <span key={index} className="tag-pill tag-pill--brand">
                                        {tag}
                                        <button
                                            type="button"
                                            aria-label={`Remove tag ${tag}`}
                                            onClick={() => handleRemoveTag(tag)}
                                        >
                                            x
                                        </button>
                                    </span>
                                ))
                            )}
                        </div>
                        <div className="tag-input-group">
                            <input
                                ref={newTagRef}
                                type="text"
                                className="form-control"
                                placeholder="Add new tag"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAddTag();
                                    }
                                }}
                            />
                            <button className="btn btn-secondary" type="button" onClick={handleAddTag}>
                                Add tag
                            </button>
                        </div>
                    </div>

                    {recipe.ingredients && recipe.ingredients.length > 0 && (
                        <div className="mb-4">
                            <h3>Ingredients</h3>
                            <ul className="list-group">
                                {recipe.ingredients.map((item, index) => (
                                    <li key={index} className="list-group-item">{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {recipe.instructions && recipe.instructions.length > 0 && (
                        <div className="mb-4">
                            <h3>Instructions</h3>
                            <ol className="list-group">
                                {recipe.instructions.map((item, index) => (
                                    <li key={index} className="list-group-item">{item}</li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {recipe.url && (
                        <p className="text-muted">
                            Original recipe:{' '}
                            <a href={recipe.url} target="_blank" rel="noopener noreferrer">{recipe.url}</a>
                        </p>
                    )}
                </div>
                <div className="card-footer text-center">
                    <Link to="/saved-recipes" className="btn btn-secondary">Back to saved recipes</Link>
                </div>
            </div>
        </div>
    );
}

export default SavedRecipeDetail;
